import type { RaRecord } from "ra-core";

/** The OPTICERCLE subscription of this CRM, as the server keeps it from Stripe. */
export type BillingStatus = {
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  /** Stripe's subscription status, or "none" before the first payment. */
  status: string;
  plan_name: string | null;
  amount: number | null;
  currency: string | null;
  current_period_end: string | null;
  cancel_at: string | null;
  open_invoice_url: string | null;
  unpaid_since: string | null;
  grace_until: string | null;
  /** Emergency switch set on the server: no blocking until then. */
  override_until: string | null;
  updated_at: string;
} & Pick<RaRecord, "id">;

/** What the app shows for the subscription. */
export type BillingState =
  | { kind: "ok" }
  | { kind: "activate" }
  | { kind: "unpaid"; deadline: Date }
  | { kind: "ended"; deadline: Date }
  | { kind: "blocked" };

const PAID = ["active", "trialing"];
const UNPAID = ["past_due", "unpaid", "paused"];
const ENDED = ["canceled", "incomplete_expired"];

const dateOf = (value: string | null) => (value ? new Date(value) : null);

/**
 * Paid: nothing. Never paid: a banner only. Unpaid or ended: a banner until
 * the end of the grace period, then the CRM is blocked, unless the emergency
 * switch is on. Without a status (not loaded, error), nothing is blocked.
 */
export const billingState = (
  status: Pick<
    BillingStatus,
    "status" | "grace_until" | "override_until"
  > | null,
  now: Date = new Date(),
): BillingState => {
  if (!status || PAID.includes(status.status)) return { kind: "ok" };
  const isUnpaid = UNPAID.includes(status.status);
  const isEnded = ENDED.includes(status.status);
  if (!isUnpaid && !isEnded) return { kind: "activate" };

  const grace = dateOf(status.grace_until);
  const override = dateOf(status.override_until);
  const deadline =
    override && override > now && (!grace || override > grace)
      ? override
      : grace;
  if (deadline && deadline <= now) return { kind: "blocked" };
  const kind = isUnpaid ? "unpaid" : "ended";
  // No known deadline: count the grace period from now, so nothing blocks.
  return { kind, deadline: deadline ?? now };
};
